import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { Capacitor } from '@capacitor/core';
import { onAuthStateChanged } from 'firebase/auth';
import i18n from 'i18next';

import { errorToastMessage } from '../components/layout/toast-root/toast';
import {
  authApiService,
  getAuthApiErrorCode,
  getAuthApiStatus,
  isTransientAuthSignInError,
} from '../features/auth/api/auth-api';
import { useFirebaseAuth } from '../hooks/useFirebaseAuth';
import { useLazyGetExerciseOptions } from '../redux/api/exerciseConfigAPI';
import { useLazyGetExerciseResults } from '../redux/api/exercisesAPI';
import {
  resetUserApiState,
  useLazyGetMyDetails,
} from '../redux/api/users/usersApi';
import {
  enableMaintenanceMode,
  enableServiceUnavailableMode,
} from '../redux/appStatusSlice';
import { authTokenHolder } from '../shared/auth/auth-token-holder';
import { auth } from '../shared/firebase/firebase-client';
import { Paths } from '../shared/paths';
import {
  LocalStorageKey,
  LocalStorageService,
} from '../shared/storage/local-storage';
import {
  beginSyncSession,
  endSyncSession,
  logAuth,
  logLoginUserStatusChange,
  logSyncStep,
  startAuthTimer,
} from '../utils/auth/auth-debug-logger';
import {
  canUseOptimisticBackendSession,
  clearUserScopedApiCaches,
  reconcileUserCachesForFirebaseUid,
  setActiveFirebaseUid,
  setBackendSessionCache,
} from '../utils/auth/backend-session-cache';
import {
  clearSignInPageBackendSignInFlow,
  peekSignInPageBackendSignInFlow,
} from '../utils/auth/sign-in-page-backend-flow-session';
import { getSignInPushDevicePayload } from '../utils/push/sign-in-push-device';
import { scheduleDeferredStartupTask } from '../utils/startup/schedule-deferred-startup-task';

const SIGNUP_404_RETRY_DELAY_MS = 2000;
const TOKEN_RELATED_BACKEND_CODES = ['TOKEN_EXPIRED', 'TOKEN_INVALID'] as const;

type SyncBackendSessionMode = 'blocking' | 'background';

enum LoginUserStatus {
  loading = 'loading',
  notLoggedIn = 'notLoggedIn',
  notVerified = 'notVerified',
  loggedIn = 'loggedIn',
  error = 'error',
}

interface IAuthContextValue {
  loginUserStatus: LoginUserStatus;
  refreshLoginUserStatus: () => Promise<void>;
}

const AuthContext = React.createContext<IAuthContextValue>({
  loginUserStatus: LoginUserStatus.loading,
  refreshLoginUserStatus: async () => {},
});

function AuthProvider(props: { children: React.ReactNode }) {
  const dispatch = useDispatch();
  const [getExerciseOptions] = useLazyGetExerciseOptions();
  const [getExerciseResults] = useLazyGetExerciseResults();
  const [loginUserStatus, setLoginUserStatusState] = useState(
    LoginUserStatus.loading,
  );
  const loginUserStatusRef = useRef(loginUserStatus);
  loginUserStatusRef.current = loginUserStatus;

  const setLoginUserStatus = useCallback(
    (
      next: LoginUserStatus,
      reason: string,
      detail?: Record<string, unknown>,
    ) => {
      const prev = loginUserStatusRef.current;
      if (prev !== next) {
        logLoginUserStatusChange(prev, next, reason, detail);
      }
      setLoginUserStatusState(next);
    },
    [],
  );

  const { getToken, logOut } = useFirebaseAuth();
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;

  useEffect(() => {
    authTokenHolder.setTokenGetter(() => getTokenRef.current());
    return () => authTokenHolder.setTokenGetter(null);
  }, []);

  const [
    getMyDetails,
    {
      data: user,
      isError: isGetMyDetailsError,
      isFetching: isFetchingMyDetails,
    },
  ] = useLazyGetMyDetails();
  const hasPrefetchedExerciseDataRef = useRef(false);
  const lastSyncedUidRef = useRef<string | null>(null);
  const backgroundSyncInFlightRef = useRef<string | null>(null);
  const optimisticGetMyDetailsUidRef = useRef<string | null>(null);
  const getMyDetailsRequestRef = useRef({
    isFetching: false,
    isError: false,
  });
  getMyDetailsRequestRef.current = {
    isFetching: isFetchingMyDetails,
    isError: isGetMyDetailsError,
  };
  const syncBackendSessionRef = useRef<
    (uid: string, mode: SyncBackendSessionMode) => Promise<void>
  >(null!);
  const logOutRef = useRef<() => Promise<void>>(null!);

  useEffect(() => {
    if (isGetMyDetailsError) {
      setLoginUserStatus(LoginUserStatus.error, 'getMyDetailsError', {
        isFetchingMyDetails,
      });
    }
  }, [isGetMyDetailsError, isFetchingMyDetails, setLoginUserStatus]);

  const addAuthStateChangedListener = useCallback(
    (
      callback: (
        fbUser: { uid: string; emailVerified: boolean } | null,
      ) => void,
    ) => {
      const isNative = Capacitor.getPlatform() !== 'web';
      if (isNative) {
        const listener = FirebaseAuthentication.addListener(
          'authStateChange',
          (event) => {
            logAuth('nativeAuth:authStateChange', {
              hasUser: !!event?.user,
              uid: event?.user?.uid,
            });
            callback(event?.user ?? null);
          },
        );

        FirebaseAuthentication.getCurrentUser()
          .then((result) => {
            logAuth('nativeAuth:getCurrentUser', {
              hasUser: !!result?.user,
              uid: result?.user?.uid,
            });
            callback(result?.user ?? null);
          })
          .catch((error) => {
            console.warn('getCurrentUser error', error);
            logAuth('nativeAuth:getCurrentUser:error', {
              message: error instanceof Error ? error.message : String(error),
            });
            callback(null);
          });

        return () => {
          listener.then((handler) => handler.remove());
        };
      }
      return onAuthStateChanged(auth, callback);
    },
    [],
  );

  const syncBackendSession = useCallback(
    async (uid: string, mode: SyncBackendSessionMode) => {
      const sessionId = beginSyncSession(uid, mode);
      startAuthTimer(`sync:${sessionId}`);

      try {
        const fromSignInPage = peekSignInPageBackendSignInFlow();
        const pushDevice = getSignInPushDevicePayload();
        const signInBody = { uid, ...(pushDevice ? { pushDevice } : {}) };
        const forceRefreshToken = mode === 'blocking';

        logSyncStep(sessionId, 'init', {
          fromSignInPage,
          forceRefreshToken,
          hasPushDevice: !!pushDevice,
          pathname:
            typeof window !== 'undefined'
              ? window.location.pathname
              : undefined,
        });

        if (mode === 'blocking') {
          if (fromSignInPage) {
            clearUserScopedApiCaches();
          }
          logSyncStep(sessionId, 'resetUserApiState');
          dispatch(resetUserApiState());
        }

        const getFirebaseToken = async (
          forceRefresh: boolean,
        ): Promise<string> => {
          const isNative = Capacitor.getPlatform() !== 'web';
          logSyncStep(sessionId, 'getIdToken:start', {
            forceRefresh,
            isNative,
          });
          startAuthTimer(`sync:${sessionId}:getIdToken`);

          let token: string | undefined;
          if (isNative) {
            const result = await FirebaseAuthentication.getIdToken({
              forceRefresh,
            });
            token = result.token;
          } else {
            const currentUser = auth.currentUser;
            if (!currentUser) {
              throw new Error('No token');
            }
            token = await currentUser.getIdToken(forceRefresh);
          }

          if (!token) {
            throw new Error('No token');
          }

          logSyncStep(sessionId, 'getIdToken:done', {
            tokenLength: token.length,
          });
          return token;
        };

        const isTokenAuthError = (error: unknown): boolean =>
          getAuthApiStatus(error) === 401 &&
          TOKEN_RELATED_BACKEND_CODES.some(
            (code) => code === getAuthApiErrorCode(error),
          );

        const signInWithRetry = async (): Promise<void> => {
          try {
            const token = await getFirebaseToken(forceRefreshToken);
            logSyncStep(sessionId, 'signIn:start');
            startAuthTimer(`sync:${sessionId}:signIn`);
            await authApiService.signIn(signInBody, token);
            logSyncStep(sessionId, 'signIn:done');
          } catch (e) {
            const status = getAuthApiStatus(e);
            const errorCode = getAuthApiErrorCode(e);
            logSyncStep(sessionId, 'signIn:error', { status, errorCode });
            const isSignUpPath =
              typeof window !== 'undefined' &&
              window.location.pathname.includes(Paths.signUp);
            if (isSignUpPath && status === 404) {
              logSyncStep(sessionId, 'signIn:retrySignUp404');
              await new Promise((r) =>
                setTimeout(r, SIGNUP_404_RETRY_DELAY_MS),
              );
              const newToken = await getFirebaseToken(true);
              await authApiService.signIn(signInBody, newToken);
              return;
            }
            if (isTokenAuthError(e)) {
              logSyncStep(sessionId, 'signIn:retryTokenAuth');
              const retryToken = await getFirebaseToken(true);
              await authApiService.signIn(signInBody, retryToken);
              return;
            }
            throw e;
          }
        };

        await signInWithRetry();
        setBackendSessionCache(uid);
        if (fromSignInPage) {
          clearSignInPageBackendSignInFlow();
        }
        if (mode === 'blocking') {
          setLoginUserStatus(
            LoginUserStatus.loggedIn,
            'syncBackendSession:blockingSuccess',
            {
              sessionId,
            },
          );
        }

        const { isFetching, isError } = getMyDetailsRequestRef.current;
        const hasOptimisticMeFetch =
          mode === 'background' && optimisticGetMyDetailsUidRef.current === uid;
        const shouldSkipMyDetailsRefresh =
          hasOptimisticMeFetch && (isFetching || !isError);

        logSyncStep(sessionId, 'getMyDetails:decision', {
          shouldSkipMyDetailsRefresh,
          isFetching,
          isError,
          hasOptimisticMeFetch,
        });

        if (!shouldSkipMyDetailsRefresh) {
          getMyDetails();
        }

        endSyncSession(sessionId, 'success', { mode });
      } catch (error) {
        endSyncSession(sessionId, 'error', {
          mode,
          status: getAuthApiStatus(error),
          errorCode: getAuthApiErrorCode(error),
          message: error instanceof Error ? error.message : String(error),
        });
        throw error;
      }
    },
    [dispatch, getMyDetails, setLoginUserStatus],
  );

  syncBackendSessionRef.current = syncBackendSession;
  logOutRef.current = logOut;

  const refreshLoginUserStatus = useCallback(async () => {
    logAuth('refreshLoginUserStatus:start');
    const fbUser = auth.currentUser;
    if (!fbUser) {
      lastSyncedUidRef.current = null;
      backgroundSyncInFlightRef.current = null;
      setLoginUserStatus(
        LoginUserStatus.notLoggedIn,
        'refreshLoginUserStatus:noFbUser',
      );
      return;
    }

    await fbUser.reload();
    if (!fbUser.emailVerified) {
      setLoginUserStatus(
        LoginUserStatus.notVerified,
        'refreshLoginUserStatus:notVerified',
      );
      return;
    }

    lastSyncedUidRef.current = fbUser.uid;
    backgroundSyncInFlightRef.current = null;
    setLoginUserStatus(
      LoginUserStatus.loading,
      'refreshLoginUserStatus:blockingSync',
    );
    await syncBackendSessionRef.current(fbUser.uid, 'blocking');
  }, [setLoginUserStatus]);

  useEffect(() => {
    const clearUserClientData = () => {
      LocalStorageService.removeValue(LocalStorageKey.pushFcmToken);
      LocalStorageService.removeValue(
        LocalStorageKey.pushPermissionPromptCompleted,
      );
      clearSignInPageBackendSignInFlow();
    };

    const forceLogoutAndRedirect = async () => {
      logAuth('forceLogoutAndRedirect:start', {
        lastSyncedUid: lastSyncedUidRef.current,
        backgroundSyncInFlight: backgroundSyncInFlightRef.current,
      });
      try {
        await logOutRef.current();
      } catch (logoutError) {
        console.error('Logout error', logoutError);
        logAuth('forceLogoutAndRedirect:logoutError', {
          message:
            logoutError instanceof Error
              ? logoutError.message
              : String(logoutError),
        });
      } finally {
        clearUserClientData();
        setActiveFirebaseUid(null);
        reconcileUserCachesForFirebaseUid(null);
        optimisticGetMyDetailsUidRef.current = null;
        dispatch(resetUserApiState());
        backgroundSyncInFlightRef.current = null;
        setLoginUserStatus(
          LoginUserStatus.notLoggedIn,
          'forceLogoutAndRedirect',
        );
        logAuth('forceLogoutAndRedirect:done', {
          lastSyncedUid: lastSyncedUidRef.current,
        });
      }
    };

    const isTokenAuthError = (error: unknown): boolean =>
      getAuthApiStatus(error) === 401 &&
      TOKEN_RELATED_BACKEND_CODES.some(
        (code) => code === getAuthApiErrorCode(error),
      );

    const handleBackendSignInFailure = (
      e: unknown,
      options?: { isOptimistic?: boolean },
    ) => {
      const status = getAuthApiStatus(e);
      const errorCode = getAuthApiErrorCode(e);
      const isOptimistic = options?.isOptimistic ?? false;

      logAuth('handleBackendSignInFailure', {
        status,
        errorCode,
        isOptimistic,
        isTransient: isTransientAuthSignInError(e),
        lastSyncedUid: lastSyncedUidRef.current,
        backgroundSyncInFlight: backgroundSyncInFlightRef.current,
        loginUserStatus: loginUserStatusRef.current,
      });

      if (status === 599) {
        lastSyncedUidRef.current = null;
        backgroundSyncInFlightRef.current = null;
        dispatch(enableMaintenanceMode());
        setLoginUserStatus(LoginUserStatus.error, 'signInFailure:maintenance');
        return;
      }
      if (status === 529) {
        lastSyncedUidRef.current = null;
        backgroundSyncInFlightRef.current = null;
        dispatch(enableServiceUnavailableMode());
        setLoginUserStatus(
          LoginUserStatus.error,
          'signInFailure:serviceUnavailable',
        );
        return;
      }

      if (isOptimistic && (isTransientAuthSignInError(e) || status >= 500)) {
        console.error('Background auth signIn error', e);
        errorToastMessage(i18n.t('common.retry'));
        logAuth('handleBackendSignInFailure:optimisticTransientIgnored');
        return;
      }

      lastSyncedUidRef.current = null;
      backgroundSyncInFlightRef.current = null;
      console.error('Auth signIn error', e);
      if (status === 404 || status === 403) {
        errorToastMessage(i18n.t('signIn.needSignUp'));
      } else if (status === 412) {
        errorToastMessage(i18n.t('BE_errors.USER_NOT_VERIFIED'));
      } else if (isTransientAuthSignInError(e)) {
        errorToastMessage(i18n.t('common.retry'));
      } else if (isTokenAuthError(e)) {
        logAuth('handleBackendSignInFailure:tokenAuthError→forceLogout');
        void forceLogoutAndRedirect();
        return;
      }
      if (status === undefined || status >= 500) {
        setLoginUserStatus(LoginUserStatus.error, 'signInFailure:serverError', {
          status,
        });
        return;
      }
      if (status !== undefined && status < 500) {
        logAuth('handleBackendSignInFailure:clientError→logout', { status });
        void logOutRef.current();
        setLoginUserStatus(
          LoginUserStatus.notLoggedIn,
          'signInFailure:clientError',
          {
            status,
          },
        );
        return;
      }
      setLoginUserStatus(LoginUserStatus.error, 'signInFailure:fallback');
    };

    const unsubscribe = addAuthStateChangedListener((fbUser) => {
      logAuth('authStateChanged', {
        hasUser: !!fbUser,
        uid: fbUser?.uid,
        emailVerified: fbUser?.emailVerified,
        lastSyncedUid: lastSyncedUidRef.current,
        backgroundSyncInFlight: backgroundSyncInFlightRef.current,
        loginUserStatus: loginUserStatusRef.current,
        platform: Capacitor.getPlatform(),
      });

      if (!fbUser) {
        setActiveFirebaseUid(null);
        reconcileUserCachesForFirebaseUid(null);
        lastSyncedUidRef.current = null;
        backgroundSyncInFlightRef.current = null;
        optimisticGetMyDetailsUidRef.current = null;
        setLoginUserStatus(
          LoginUserStatus.notLoggedIn,
          'authStateChanged:nullUser',
        );
        return;
      }

      setActiveFirebaseUid(fbUser.uid);
      reconcileUserCachesForFirebaseUid(fbUser.uid);

      if (!fbUser.emailVerified) {
        setLoginUserStatus(
          LoginUserStatus.notVerified,
          'authStateChanged:notVerified',
        );
        return;
      }

      if (lastSyncedUidRef.current === fbUser.uid) {
        logAuth('authStateChanged:skipDuplicateUid', {
          uid: fbUser.uid,
          loginUserStatus: loginUserStatusRef.current,
        });
        return;
      }
      lastSyncedUidRef.current = fbUser.uid;

      const fromSignInPage = peekSignInPageBackendSignInFlow();
      const useOptimisticPath =
        !fromSignInPage && canUseOptimisticBackendSession(fbUser.uid);

      logAuth('authStateChanged:pathDecision', {
        uid: fbUser.uid,
        fromSignInPage,
        useOptimisticPath,
      });

      if (useOptimisticPath) {
        setLoginUserStatus(
          LoginUserStatus.loggedIn,
          'authStateChanged:optimistic',
        );
        optimisticGetMyDetailsUidRef.current = fbUser.uid;
        getMyDetails();

        if (backgroundSyncInFlightRef.current === fbUser.uid) {
          logAuth('authStateChanged:skipBackgroundSyncInFlight', {
            uid: fbUser.uid,
          });
          return;
        }

        backgroundSyncInFlightRef.current = fbUser.uid;
        syncBackendSessionRef
          .current(fbUser.uid, 'background')
          .catch((e) => handleBackendSignInFailure(e, { isOptimistic: true }))
          .finally(() => {
            if (backgroundSyncInFlightRef.current === fbUser.uid) {
              backgroundSyncInFlightRef.current = null;
              logAuth('authStateChanged:backgroundSyncDone', {
                uid: fbUser.uid,
              });
            }
          });
        return;
      }

      setLoginUserStatus(
        LoginUserStatus.loading,
        'authStateChanged:blockingSync',
      );
      syncBackendSessionRef
        .current(fbUser.uid, 'blocking')
        .catch((e) => handleBackendSignInFailure(e));
    });
    return () => {
      unsubscribe();
    };
  }, [addAuthStateChangedListener, dispatch, getMyDetails, setLoginUserStatus]);

  useEffect(() => {
    if (loginUserStatus !== LoginUserStatus.loggedIn || !user?.id) {
      hasPrefetchedExerciseDataRef.current = false;
      return;
    }

    if (hasPrefetchedExerciseDataRef.current) {
      return;
    }

    hasPrefetchedExerciseDataRef.current = true;
    const cancelDeferredPrefetch = scheduleDeferredStartupTask(() => {
      getExerciseOptions();
      getExerciseResults();
    });

    return cancelDeferredPrefetch;
  }, [loginUserStatus, user?.id, getExerciseOptions, getExerciseResults]);

  return (
    <AuthContext.Provider value={{ loginUserStatus, refreshLoginUserStatus }}>
      {props.children}
    </AuthContext.Provider>
  );
}

function useAuth() {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

export { useAuth, AuthProvider, LoginUserStatus };
